import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";
import _ from "lodash";
import { SFUseStyled, SFStack, SFTypography, SFHelpers } from "@sf-core";

const { styled } = SFUseStyled();

const StyledCircle = styled("div")(({ theme, color }) => ({
  width: 7,
  height: 7,
  borderRadius: "50%",
  backgroundColor: color,
}));

const TooltipStyled = styled("div")(({ theme }) => ({
  background: "rgba(255,255,255,0.98)",
  filter: "drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.25))",
  border: "1px rgba(0, 0, 0, 0.25) solid",
  padding: theme.spacing(1),
  borderRadius: 4,
  color: theme.palette.grey400.main,
  maxWidth: 340,
  ".title": {
    fontSize: "14px",
    fontWeight: 600,
  },
  ".sub-title": {
    fontSize: "11px",
    fontWeight: 300,
  },
  ".skill-item": {
    fontSize: "12px",
    fontWeight: 300,
  },
}));

const CustomTooltip = ({ active, payload, label, t, ...props }) => {
  if (active && payload && payload.length) {
    // Map through each payload item, format the duration, and accumulate the total
    const formattedPayload = payload
      .filter((p) => p.name !== "total")
      .map((p) => ({
        name: p.name,
        color: p.color,
        formattedValue: SFHelpers.DurationFormatter(
          p.value,
          SFHelpers.DurationFormatterOptions.HOURS_ONLY
        ),
        value: p.value,
      }));

    // Calculate the total duration after formatting
    const totalFormattedDuration = formattedPayload
      .reduce((acc, item) => acc + item.formattedValue, 0)
      .toFixed(1);

    // Extract the full date from the payload's payload.value if it exists
    const fullDate = payload[0]?.payload?.fullDate || label;

    return (
      <TooltipStyled>
        <SFStack spacing={2}>
          <SFStack>
            <SFTypography className="title">
              {t("dashboardScreen.timeInvestment.title", {
                count: totalFormattedDuration,
              })}
            </SFTypography>
            <SFTypography className="sub-title">{fullDate}</SFTypography>
          </SFStack>
          <SFStack>
            {formattedPayload.reverse().map((p, index) => (
              <SFStack
                key={index}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <StyledCircle color={p.color} />
                <SFTypography className="skill-item">
                  {t("dashboardScreen.timeInvestment.skillItem", {
                    count: p.formattedValue,
                    name: p.name,
                  })}
                </SFTypography>
              </SFStack>
            ))}
          </SFStack>
        </SFStack>
      </TooltipStyled>
    );
  }

  return null;
};

const TimeInvestment = ({ items }) => {
  const { t } = useTranslation();
  let stackId = "a";
  let legendType = "none";
  const colorCycle = [
    "rgba(135, 211, 242, 0.8)",
    "rgba(78, 190, 235, 0.8)",
    "rgba(53, 164, 232, 0.8)",
    "rgba(24, 140, 229, 0.8)",
  ];

  // Padding for the line chart above the bar chart
  let padding = 0;

  const newItems = useMemo(() => {
    const endDate = moment().startOf("month");
    const startDate = moment().subtract(12, "months").startOf("month");
    // Create an array for each month in the range
    const months = [];
    for (
      let m = moment(startDate);
      m.isSameOrBefore(endDate);
      m.add(1, "months")
    ) {
      const isJanuary = m.month() === 0;
      const formattedDate = isJanuary ? m.format("MMM YYYY") : m.format("MMM");
      months.push({
        monthKey: m.format("YYYY-MM"),
        formattedDate: formattedDate,
      });
    }

    // Group items by month
    const groupedByMonth = _.groupBy(items, (item) =>
      moment(item.timestamp).format("YYYY-MM")
    );

    // Calculate the total duration for each skill in each month
    const data = months.map(({ monthKey, formattedDate }) => {
      const itemsForMonth = groupedByMonth[monthKey] || [];
      const skillsDuration = itemsForMonth.reduce((acc, item) => {
        acc[item.skillName] = (acc[item.skillName] || 0) + item.duration;
        return acc;
      }, {});

      // Include the fullDate only for the tooltip, not as part of the chart's data
      return {
        date: formattedDate, // Abbreviated month name for the chart
        ...skillsDuration,
        total: _.sum(Object.values(skillsDuration)),
        fullDate: moment(monthKey, "YYYY-MM").format("MMMM YYYY"), // Full month name and year for the tooltip
      };
    });

    // Find the maximum total value
    const maxTotal = Math.max(...data.map((item) => item.total));
    padding = 0.05 * maxTotal;
    // Create new data with total values updated by adding padding for the line
    const updatedData = data.map((item) => ({
      ...item,
      total: item.total + padding,
    }));

    return updatedData;
  }, [items]);

  // Get a list of all unique skills to create bars for each skill
  const uniqueSkills = useMemo(() => {
    const allSkills = newItems.flatMap((item) =>
      Object.keys(item).filter(
        (key) => key !== "date" && key !== "total" && key !== "fullDate"
      )
    );
    return _.uniq(allSkills);
  }, [newItems]);

  // Assign a color from the cycle to each skill
  const skillColors = uniqueSkills.reduce((acc, skill, index) => {
    acc[skill] = colorCycle[index % colorCycle.length];
    return acc;
  }, {});

  return (
    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart width="100%" height={400} data={newItems}>
        <XAxis dataKey="date" axisLine={false} tickLine={false} />
        <YAxis domain={[0, `dataMax + ${padding}`]} hide />
        <Tooltip content={<CustomTooltip t={t} padding={padding} />} />
        {/* Render a Bar for each unique skill */}
        {uniqueSkills.map((skill) => (
          <Bar
            key={skill}
            dataKey={skill}
            name={skill}
            stackId={stackId}
            fill={skillColors[skill]}
            barSize={30}
          />
        ))}
        <Line
          type="monotone"
          dataKey="total"
          stroke="rgba(78, 190, 235, 1)"
          strokeWidth={4}
          dot={{
            stroke: "rgba(53, 164, 232, 1)",
            fill: "rgba(46, 46, 56, 1)",
            r: 8,
            strokeWidth: 4,
          }}
          legendType={legendType}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default TimeInvestment;
